import React, { useCallback, useEffect, useState } from "react";
import { unmountComponentAtNode } from "react-dom";
import SwiperModule from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { IImage, IVideo } from "types";
import { Modal, ImageSlide, VideoSlide } from "components";
import { getRenderRoot, RenderRoot } from "utils";
import { lazyLoadService } from "services";

interface GalleryModalProps {
  images?: IImage[];
  videos?: IVideo[];
  initIndex?: number;
}

export const GalleryModal: React.FC<GalleryModalProps> = ({
  images = [],
  videos = [],
  initIndex = 0,
}) => {
  const [swiper, setSwiper] = useState<SwiperModule | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(initIndex);

  // unmount modal after close
  useEffect(() => {
    const modalRoot = getRenderRoot(RenderRoot.modal);

    if (!isOpen && modalRoot) {
      unmountComponentAtNode(modalRoot);
    }
  }, [isOpen]);

  // observe lazy loading images
  useEffect(() => {
    lazyLoadService.observe();
  }, [swiper, currentIndex]);

  const slideProps = useCallback(
    (index: number, length: number) => ({
      isActive: currentIndex === index,
      currentSlide: currentIndex + 1,
      totalSlides: length,
      isPrevActive: currentIndex > 0,
      isNextActive: currentIndex < length - 1,
      handlePrevClick: () => swiper?.slidePrev(),
      handleNextClick: () => swiper?.slideNext(),
    }),
    [swiper, currentIndex]
  );

  if (!isOpen) return <></>;
  if (!images?.length && !videos?.length) return <></>;

  const renderImages = (
    <>
      {images.map((image, index) => (
        <SwiperSlide key={`image-${index}`}>
          <ImageSlide {...image} {...slideProps(index, images.length)} />
        </SwiperSlide>
      ))}
    </>
  );

  const renderVideos = (
    <>
      {videos.map((video, index) => (
        <SwiperSlide key={`image-${index}`}>
          <VideoSlide {...video} {...slideProps(index, videos.length)} />
        </SwiperSlide>
      ))}
    </>
  );

  return (
    <Modal handleClose={() => setIsOpen(false)}>
      <Swiper
        initialSlide={initIndex}
        onSwiper={(s) => {
          setSwiper(s);
        }}
        onSlideChange={(s) => setCurrentIndex(s.activeIndex)}
        keyboard={{
          enabled: true,
        }}
      >
        {images && renderImages}
        {videos && renderVideos}
      </Swiper>
    </Modal>
  );
};
